import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { translations, type Locale } from './translations';

interface I18nContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => string;
}

const I18nContext = createContext<I18nContextType>({
  locale: 'en',
  setLocale: () => {},
  t: (key) => key,
});

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const saved = localStorage.getItem('mvp-profile');
    if (saved) {
      try {
        const profile = JSON.parse(saved);
        if (profile.language === 'fr') return 'fr';
      } catch {}
    }
    return 'en';
  });

  const setLocale = useCallback((newLocale: Locale) => {
    setLocaleState(newLocale);
    // Also update the saved profile
    try {
      const saved = localStorage.getItem('mvp-profile');
      const profile = saved ? JSON.parse(saved) : {};
      profile.language = newLocale;
      localStorage.setItem('mvp-profile', JSON.stringify(profile));
    } catch {}
  }, []);

  const t = useCallback((key: string): string => {
    return translations[locale]?.[key] ?? translations['en']?.[key] ?? key;
  }, [locale]);

  return (
    <I18nContext.Provider value={{ locale, setLocale, t }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
